import { useCallback, useState } from 'react';

import { Trans, t } from '@lingui/macro';
import PropTypes from 'prop-types';
import { Divider } from 'semantic-ui-react';
import styled from 'styled-components';

import { api } from 'actions/utils';

import { LightHeader, MediumHeader } from 'components/ui/Header';
import LightText from 'components/ui/Text';
import FlagItem from 'components/ui/cells/FlagItem';
import { AnalyticsAwareHoverableIconButtonWithTooltip } from 'components/ui/icon/HoverableIcon';
import RichTextEditor, {
  LoadingRichTextEditor,
} from 'components/ui/inputs/RichTextEditor';
import { Column } from 'components/ui/layout/Page';
import RowMenu from 'components/ui/management-list/RowMenu';

import commonPropTypes from 'utils/commonPropTypes';
import renderHtml from 'utils/html';
import capitalizedTranslation from 'utils/i18n';
import { isRtlLanguage } from 'utils/types';

import * as svars from 'assets/style/variables';

const ElementContainer = styled.span`
  display: flex;
  align-items: center;
  overflow: hidden;
  flex-grow: 1;
`;

export const languageElementFactory = (onDelete, loading) => {
  function LanguageElement({ item, index }) {
    return (
      <div style={{ display: 'flex' }}>
        <ElementContainer>
          <FlagItem language={item} />
        </ElementContainer>
        <RowMenu
          upward={index > 2}
          items={[
            {
              content: t({ id: 'delete' }),
              icon: 'delete',
              iconColor: 'red',
              disabled: loading,
              onClick: onDelete(item),
            },
          ]}
          disabled={false}
        />
      </div>
    );
  }
  LanguageElement.propTypes = {
    item: PropTypes.string.isRequired,
    index: PropTypes.number.isRequired,
  };
  LanguageElement.defaultProps = {};
  return LanguageElement;
};

const handleAutoTranslate = async (
  text,
  originalLanguage,
  targetLanguage,
  onChange,
  setLoading,
  callback
) => {
  setLoading(true);
  let response;
  try {
    response = await api.post(`/translate/v1`, {
      text,
      source: originalLanguage,
      target: targetLanguage,
    });
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Translation error:', error);
  }
  if (response) {
    onChange(response.data.text);
    callback?.();
  }
};

export function TranslationElement({
  originalText,
  translatedText,
  onChange,
  originalLanguage,
  targetLanguage,
  label,
  singleLine,
  basic,
  hideLabel,
  undeprecate,
  deprecated,
  withDivider,
}) {
  const [loading, setLoading] = useState(false);
  const onAutoTranslate = useCallback(
    () =>
      handleAutoTranslate(
        originalText?.trim(),
        originalLanguage,
        targetLanguage,
        onChange,
        setLoading,
        () => setTimeout(() => setLoading(false), 0)
      ),
    [onChange, originalText, targetLanguage, originalLanguage]
  );
  if (!originalText?.trim()) {
    return null;
  }
  return (
    <div style={{ margin: `0 ${svars.spaceNormal}` }}>
      <LightHeader
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: `${svars.spaceMedium} 0 0 0`,
        }}
      >
        {!hideLabel ? (
          <Trans id={label} render={capitalizedTranslation} />
        ) : null}
      </LightHeader>

      <div style={{ width: '100%', display: 'flex', flexDirection: 'row' }}>
        <Column
          width={7.35}
          style={{ margin: `0`, paddingRight: svars.spaceNormal }}
        >
          {originalText?.trim() ? (
            renderHtml(originalText, null, null, null, true, true)
          ) : (
            <LightText>
              <Trans id="(empty)" />
            </LightText>
          )}
        </Column>
        <Column
          width={7.35}
          layout="row"
          style={{ margin: 0, maxHeight: '300px' }}
        >
          {loading ? (
            <LoadingRichTextEditor isSubtitle singleLine={singleLine} />
          ) : (
            <RichTextEditor
              rtl={isRtlLanguage(targetLanguage)}
              content={translatedText}
              onChange={onChange}
              isSubtitle
              placeholder={label}
              singleLine={singleLine}
              basic={basic}
              withLink={!singleLine && !basic}
              withTextAlign={!singleLine && !basic}
            />
          )}
        </Column>
        <Column layout="row" style={{ alignItems: 'center', margin: 'auto 0' }}>
          <AnalyticsAwareHoverableIconButtonWithTooltip
            help={<Trans id="campaign-customization.auto-translate-text" />}
            gaCategory="Campaign - customization"
            gaAction="Translate"
            gaLabel={`${targetLanguage}`}
            name="magic"
            onClick={() => onAutoTranslate(originalText?.trim())}
            disabled={!undeprecate}
          />
          {deprecated ? (
            <AnalyticsAwareHoverableIconButtonWithTooltip
              help={
                <Trans id="campaign-customization.text-changed-translation-deprecated" />
              }
              gaCategory="Campaign - customization"
              gaAction="Undeprecate"
              gaLabel={`${targetLanguage}`}
              name="warning circle"
              onClick={() => undeprecate()}
              disabled={!undeprecate}
              color={svars.colorYellowWarning}
            />
          ) : null}
          {/* <AnalyticsAwareHoverableIconButtonWithTooltip
            help={<Trans id="copy-into-translation" />}
            disabled={!originalText?.trim()}
            gaCategory="Campaign - customization"
            gaAction="Translate"
            gaLabel={`${targetLanguage}`}
            name="long arrow alternate right"
            onClick={copyOriginalTextToTranslatedText}
          /> */}
        </Column>
      </div>
      {withDivider ? <Divider fitted /> : null}
    </div>
  );
}

TranslationElement.propTypes = {
  originalText: PropTypes.string.isRequired,
  translatedText: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  targetLanguage: PropTypes.string.isRequired,
  label: commonPropTypes.i18nText,
  singleLine: PropTypes.bool,
  basic: PropTypes.bool,
  hideLabel: PropTypes.bool,
  undeprecate: PropTypes.func.isRequired,
  deprecated: PropTypes.bool.isRequired,
  originalLanguage: PropTypes.string.isRequired,
  withDivider: PropTypes.bool,
};
TranslationElement.defaultProps = {
  label: null,
  singleLine: false,
  basic: false,
  hideLabel: false,
  withDivider: false,
};

const getTranslatedValue = (translations, language) => {
  const found = translations?.find(
    (translation) => translation.language === language
  );
  return found?.text || '';
};

const getTranslatedIsDeprecated = (translations, language) => {
  const found = translations?.find(
    (translation) => translation.language === language
  );
  return found?.deprecated || false;
};
export function TranslationBlock({
  element,
  fields,
  header,
  originalLanguage,
  selectedExtraLanguage,
  onTranslate,
  headerBackground,
  loading,
}) {
  return (
    <span
      style={{
        display: 'flex',
        flexDirection: 'row',
        marginRight: svars.spaceNormal,
        width: '100%',
        borderBottom: svars.strongBorderStyle,
      }}
    >
      <MediumHeader
        style={{
          background: headerBackground,
          padding: `${svars.spaceMedium} ${svars.spaceNormalLarge}`,
          maxWidth: '85px',
          minWidth: '85px',
          margin: 0,
        }}
      >
        {header}
      </MediumHeader>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          borderLeft: svars.lightBorderStyle,
        }}
      >
        {fields.map(
          (
            {
              multiKey,
              singleLine,
              basic,
              fieldName,
              translationsFieldName,
              label,
            },
            iField
          ) =>
            multiKey ? (
              element[multiKey]?.map((subElement, i) => (
                <TranslationElement
                  key={`trans-${multiKey}-${fieldName}-${selectedExtraLanguage}-${subElement.id || i}`}
                  hideLabel={i !== 0}
                  originalLanguage={originalLanguage}
                  targetLanguage={selectedExtraLanguage}
                  originalText={subElement[fieldName]}
                  translatedText={getTranslatedValue(
                    subElement[translationsFieldName],
                    selectedExtraLanguage
                  )}
                  undeprecate={onTranslate(
                    translationsFieldName,
                    multiKey,
                    fieldName,
                    subElement.id
                  )}
                  deprecated={getTranslatedIsDeprecated(
                    subElement[translationsFieldName],
                    selectedExtraLanguage
                  )}
                  onChange={onTranslate(
                    translationsFieldName,
                    multiKey,
                    fieldName,
                    subElement.id
                  )}
                  label={label}
                  singleLine
                  loading={loading}
                  withDivider={i < element[multiKey].length - 1}
                />
              ))
            ) : (
              <TranslationElement
                key={`trans-${fieldName}-map-${selectedExtraLanguage}`}
                originalLanguage={originalLanguage}
                targetLanguage={selectedExtraLanguage}
                originalText={element[fieldName]}
                translatedText={getTranslatedValue(
                  element[translationsFieldName],
                  selectedExtraLanguage
                )}
                onChange={onTranslate(translationsFieldName)}
                label={label}
                singleLine={singleLine}
                basic={basic}
                style={{ margin: `0 ${svars.spaceNormal}` }}
                undeprecate={onTranslate(translationsFieldName)}
                deprecated={getTranslatedIsDeprecated(
                  element[translationsFieldName],
                  selectedExtraLanguage
                )}
                loading={loading}
                withDivider={iField < fields.length - 1}
              />
            )
        )}
      </div>
    </span>
  );
}

TranslationBlock.propTypes = {
  element: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
  }),
  fields: PropTypes.arrayOf(
    PropTypes.shape({
      fieldName: PropTypes.string.isRequired,
      translationsFieldName: PropTypes.string.isRequired,
    })
  ).isRequired,
  header: commonPropTypes.i18nText.isRequired,
  selectedExtraLanguage: PropTypes.string.isRequired,
  onTranslate: PropTypes.func.isRequired,
  headerBackground: PropTypes.string,
  loading: PropTypes.bool,
  originalLanguage: PropTypes.string.isRequired,
};

TranslationBlock.defaultProps = {
  element: null,
  headerBackground: svars.colorLightestGrey,
  loading: false,
};
